/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import React, { useState } from "react"
import { Drawer, Button } from "@duxton-web/ui"
import { ChevronLeftUniversal } from "@duxton-web/icons"
import { Form } from "grab-ui"
import { compact } from "lodash"
import { drawerFooter } from "components/drawers/styles"
import { ReportReviewRequest, Review } from "types/api/menuFeedback.type"
import { useDispatch, useSelector } from "react-redux"
import { useTranslationsMap } from "common/utils/i18n"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { actions as feedbackDrawerActions } from "store/duck/feedbackDrawer"
import { reportButtonTriggeredRumAction } from "store/middlewares/fetchLifecycleRum"
import { getActionSuccess, getActionFail } from "store/duck/utils/actionUtils"
import useFEConfig from "hooks/useFEConfig"
import { ServiceType } from "constants/feedback"
import { TroyWebTracker } from "utils/tracking"
import {
  EventAction,
  EventCategory,
  EventActionEng,
  EventCategoryEng,
} from "constants/tracking"
import { REPORT_POLICY_LINK } from "constants/links"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { useSubmitReportReviewMutation } from "queries"
import notification from "components/common/notification"
import { FeedbackReportReason, FeedbackReportSubmit } from "./steps"

interface FeedbackReportDrawerProps extends FormComponentProps {
  visible: boolean
  onClose(): void
  review?: Review
  form: WrappedFormUtils
}

export const FeedbackReportDrawerComponent: React.FC<FeedbackReportDrawerProps> = ({
  visible,
  onClose,
  review,
  form,
}: FeedbackReportDrawerProps) => {
  const dispatch = useDispatch()

  const {
    flags: { submitReportReview: isSubmitReportReviewRtkEnabled },
    isLoading: isLoadingRtkQueryMigrationEndpointsFlag,
  } = useRtkQueryMigrationEndpointsFlag()

  const [submitReportReviewRtk] = useSubmitReportReviewMutation()

  const textMap = useTranslationsMap({
    report: "feedback.titleBar.report",
    next: "feedback.button.next",
    submit: "feedback.button.submit",
    successMessage: "feedback.headings.success",
    successDescription: "feedback.body.success",
    errorMessage: "feedback.headings.error",
    errorDescription: "feedback.body.error",
  })

  const [step, setStep] = useState<number>(0)
  const [reasons, setReasons] = useState<string[]>([])

  const {
    feedbackDrawer: { files, isSubmitting },
  } = useSelector((state: AppState) => state)

  const nextStep = () => {
    setStep(step + 1)

    TroyWebTracker.sendEvent({
      category: EventCategory.feedbackReport,
      action: EventAction.clickNext,
      labels: {
        REVIEW_ID: review?.reviewID,
        REVIEW_RATING: review?.rating,
        REPORT_REASON: reasons,
      },
    })
  }

  const prevStep = () => setStep(Math.max(step - 1, 0))

  const handleSubmit = () => {
    if (!review) return
    form.validateFields((err: any, values: any) => {
      if (err) return

      reportButtonTriggeredRumAction({
        trigger: "feedback_submit_report_review",
        endpoint_name: "submitReportReview",
      })

      const imageUrls = files.map(file => file.url)

      const data: ReportReviewRequest = {
        reviewID: review.reviewID,
        reasons,
        imageUrls,
        imageTokens: compact(files.map(file => file.token)),
        description: values.description,
      }

      if (isSubmitReportReviewRtkEnabled) {
        // RTK Query path
        TroyWebTracker.sendEvent({
          category: EventCategoryEng.rtkMigration,
          action: EventActionEng.submitReportReview,
          labels: {
            MERCHANT_ID: review.merchantID,
            IS_RTK_ENABLED: isSubmitReportReviewRtkEnabled,
            FROM_RTK_QUERY: true,
            FROM_OLD_REDUX: false,
            ACTION_NAME: "submitReportReview",
          },
        })
        // Dispatch request action to set loading state
        dispatch({
          type: feedbackDrawerActions.SUBMIT_REPORT_REVIEW,
        })
        submitReportReviewRtk({
          merchantID: review.merchantID,
          ...data,
        })
          .unwrap()
          .then(responseData => {
            // Show success notification
            notification.success({
              message: textMap.successMessage,
              description: textMap.successDescription,
            })
            // Sync to legacy Redux state for backward compatibility
            dispatch({
              type: getActionSuccess(
                feedbackDrawerActions.SUBMIT_REPORT_REVIEW,
              ),
              payload: {
                data: responseData,
                headers: {
                  date: new Date().toISOString(),
                },
              },
              meta: {
                previousAction: {
                  payload: {
                    request: { data },
                    review,
                  },
                },
              },
            })
          })
          .catch(() => {
            // Show error notification
            notification.error({
              message: textMap.errorMessage,
              description: textMap.errorDescription,
            })
            // Dispatch fail action to clear loading state
            dispatch({
              type: getActionFail(feedbackDrawerActions.SUBMIT_REPORT_REVIEW),
              payload: {
                data: {},
                headers: {
                  date: new Date().toISOString(),
                },
              },
              meta: {
                previousAction: {
                  payload: {
                    request: { data },
                    review,
                  },
                },
              },
            })
          })
      } else {
        // Old Redux path
        TroyWebTracker.sendEvent({
          category: EventCategoryEng.rtkMigration,
          action: EventActionEng.submitReportReview,
          labels: {
            MERCHANT_ID: review.merchantID,
            IS_RTK_ENABLED: isSubmitReportReviewRtkEnabled,
            FROM_RTK_QUERY: false,
            FROM_OLD_REDUX: true,
            ACTION_NAME: "submitReportReview",
          },
        })
        dispatch(
          actionCreator.feedbackDrawer.submitReportReview({
            data,
            review,
            successMessage: textMap.successMessage,
            successDescription: textMap.successDescription,
            errorMessage: textMap.errorMessage,
            errorDescription: textMap.errorDescription,
          }),
        )
      }

      TroyWebTracker.sendEvent({
        category: EventCategory.feedbackReport,
        action: EventAction.clickSubmit,
        labels: {
          REVIEW_ID: review?.reviewID,
          REVIEW_RATING: review?.rating,
          REPORT_REASON: reasons,
          PHOTO_FILENAME: imageUrls,
          REPORT_DESCRIPTION: values.description,
        },
      })
    })
  }

  const feConfig = useFEConfig({ merchantID: review?.merchantID })

  const reasonOptions =
    (review?.serviceType === ServiceType.DINEIN ||
    review?.serviceType === ServiceType.NON_TRANSACTIONAL
      ? feConfig.data?.reviewReportConfig?.dineoutReviewReportReasons
      : feConfig.data?.reviewReportConfig?.reportReasons) ?? []

  const reasonKey = reasonOptions.reduce((acc, reason) => {
    return {
      ...acc,
      [reason.code]: `feedback.list.reportReason.${reason.code}`,
    }
  }, {})

  const reasonText = useTranslationsMap(reasonKey)

  const mapOptions = reasonOptions.map(reason => ({
    content: reasonText[reason.code] || reason.content,
    code: reason.code,
  }))
  const disableSubmit =
    !form.getFieldValue("description")?.trim() || files.length > 4

  return (
    <Drawer
      destroyOnClose
      data-testid="feedback-report-drawer"
      open={visible}
      onClose={() => {
        if (step === 0) onClose()
        else prevStep()
      }}
      closeIcon={step > 0 ? <ChevronLeftUniversal /> : true}
      title={textMap.report}
      width={475}
    >
      <div
        style={{
          display: step === 0 ? "block" : "none",
        }}
      >
        <FeedbackReportReason
          reasons={reasons}
          onChange={setReasons}
          options={mapOptions}
          reportPolicyLink={
            feConfig.data?.reviewReportConfig?.reportPolicyLink ||
            REPORT_POLICY_LINK
          }
        />
      </div>

      <div
        style={{
          display: step > 0 ? "block" : "none",
        }}
      >
        <FeedbackReportSubmit
          review={review}
          reasons={reasons}
          reasonOptions={mapOptions}
          form={form}
        />
      </div>
      <div css={drawerFooter}>
        {step === 0 ? (
          <Button
            data-testid="report-next-btn"
            type="primary"
            block
            style={{ fontWeight: 500, fontSize: 16 }}
            onClick={nextStep}
            disabled={!reasons.length}
          >
            {textMap.next}
          </Button>
        ) : (
          <Button
            data-testid="report-submit-btn"
            htmlType="submit"
            type="primary"
            block
            style={{ fontWeight: 500, fontSize: 16 }}
            onClick={handleSubmit}
            disabled={
              disableSubmit ||
              isSubmitting ||
              files.some(file => !file.token) ||
              isLoadingRtkQueryMigrationEndpointsFlag
            }
          >
            {textMap.submit}
          </Button>
        )}
      </div>
    </Drawer>
  )
}

export const FeedbackReportDrawer = Form.create<FeedbackReportDrawerProps>({})(
  FeedbackReportDrawerComponent,
)
